import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { OutreachStepsPreview } from '@apps/web/components/outreaches/outreach_steps_preview';
import { ClientIdentifyingInfoWarning } from '@apps/web/components/settings/client_identifying_info_warning';
import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Form } from '@apps/web/components/ui/form';
import { useClientIdentifyingInfoConfirmation } from '@apps/web/hooks/use_client_identifying_info_confirmation';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { getApiErrorDetail } from '@apps/web/lib/known_api_errors';
import { orderShiftsForOutreach } from '@apps/web/lib/outreach_shift_ordering';
import {
    getOutreachSmsTemplateTexts,
    getTemplateVoiceOpeningLine,
    type IdentifiedOutreachStep,
    resolveVoiceOpeningLine,
    serializeStepsForSubmission,
    usesSplitVoiceOpeningLine,
} from '@apps/web/lib/outreach_step_editor_utils';
import {
    type OutreachStep,
    splitSmsGreetingTemplate,
} from '@apps/web/lib/outreach_utils';
import {
    useOrganization,
    useOutreachTemplates,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type JoinedOutreachRead } from '@libraries/typescript/api_client';

const formSchema = z.object({
    openingLine: z.string().optional(),
    instructions: z.string().optional(),
    smsMessage: z.string().optional(),
    payRate: z.string().optional(),
    recurrenceScheduleDescription: z.string().optional(),
    multiShiftHeader: z.string().optional(),
    multiShiftItem: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

type OutreachStepsModalProps = {
    readonly isOpen: boolean;
    readonly onClose: () => void;
    readonly outreach: JoinedOutreachRead;
    readonly onSuccess: () => void;
};

export function OutreachStepsModal({
    isOpen,
    onClose,
    outreach,
    onSuccess,
}: OutreachStepsModalProps) {
    const organizationId = useOrganizationId();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'outreach-steps-modal',
    });
    const { data: templates } = useOutreachTemplates(organizationId, {
        reason: 'outreach-steps-modal',
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const {
        confirmClientIdentifyingInfo,
        clientIdentifyingInfoConfirmationDialog,
    } = useClientIdentifyingInfoConfirmation({
        clientReferenceTerm:
            organization?.general_settings?.client_reference_term ?? 'client',
        confirmLabel: 'Save Changes',
    });
    const [steps, setSteps] = useState<Array<IdentifiedOutreachStep>>(
        outreach.steps,
    );
    const hasLocalEdits = useRef(false);
    const prevIsOpen = useRef(isOpen);

    const selectedShifts = useMemo(
        () => orderShiftsForOutreach(outreach.shifts, outreach.shift_groups),
        [outreach.shift_groups, outreach.shifts],
    );

    const shiftGroups = useMemo(() => {
        const keys = outreach.outreach_metadata['shift_groups'] as
            | Array<string>
            | undefined;
        return keys ? new Set(keys) : undefined;
    }, [outreach.outreach_metadata]);

    const templateSteps = useMemo(() => {
        const matchingTemplate = templates?.find(
            (t) => t.outreach_type === outreach.outreach_type,
        );
        return matchingTemplate?.steps ?? [];
    }, [templates, outreach.outreach_type]);

    // Extract SMS message from the first SMS step
    const smsStep = useMemo(
        () => outreach.steps.find((step) => step.step_type === 'sms'),
        [outreach.steps],
    );
    const isMultiShift = selectedShifts.length > 1;
    const openingLineFromMetadata =
        getTemplateVoiceOpeningLine({
            metadataSchema: outreach.outreach_metadata,
            outreachType: outreach.outreach_type,
            isMultiShift,
        }) || '';

    // Build per-shift care plans from metadata, falling back to care_plan_bullets
    // for older outreaches that don't have the newer shift_care_plans field
    const perShiftCarePlans = useMemo(() => {
        const stored = outreach.outreach_metadata['shift_care_plans'] as
            | Record<string, { summary: string; bullets: string }>
            | undefined;
        if (stored && Object.keys(stored).length > 0) {
            return stored;
        }
        const bullets = outreach.outreach_metadata['care_plan_bullets'] as
            | string
            | undefined;
        if (!bullets) {
            return undefined;
        }
        return Object.fromEntries(
            selectedShifts.map((s) => [s.id, { summary: bullets, bullets }]),
        );
    }, [outreach.outreach_metadata, selectedShifts]);

    // Strip the greeting prefix from the SMS message for editing
    // Check callout_template (new) first, then message_template (legacy)
    const smsMessageWithoutGreeting = useMemo(() => {
        const template =
            smsStep?.callout_template || smsStep?.message_template || '';
        return splitSmsGreetingTemplate(template).messageBody;
    }, [smsStep]);

    // Extract multi-shift templates (strip greeting from header)
    const multiShiftHeaderWithoutGreeting = useMemo(() => {
        const template =
            outreach.outreach_type === 'recurring_shift'
                ? (smsStep?.group_template ?? '')
                : (smsStep?.callout_multi_shift_template ?? '');
        return splitSmsGreetingTemplate(template).messageBody;
    }, [outreach.outreach_type, smsStep]);

    const multiShiftItem = useMemo(
        () => smsStep?.multi_shift_item_template || '',
        [smsStep],
    );

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            openingLine: openingLineFromMetadata,
            instructions:
                (outreach.outreach_metadata['instructions'] as
                    | string
                    | undefined) || '',
            // Pre-populate from saved outreach if available
            smsMessage: smsMessageWithoutGreeting || '',
            payRate: '',
            recurrenceScheduleDescription:
                (outreach.outreach_metadata[
                    'recurrence_schedule_description'
                ] as string | undefined) || '',
            // Pre-populate multi-shift templates from saved outreach
            multiShiftHeader: multiShiftHeaderWithoutGreeting || '',
            multiShiftItem: multiShiftItem || '',
        },
    });

    const handleStepsChange = useCallback((newSteps: Array<OutreachStep>) => {
        hasLocalEdits.current = true;
        setSteps(newSteps);
    }, []);

    useEffect(() => {
        if (isOpen && !prevIsOpen.current) {
            hasLocalEdits.current = false;
        }
        prevIsOpen.current = isOpen;
        if (!hasLocalEdits.current) {
            setSteps(outreach.steps);
        }
    }, [isOpen, outreach.steps]);

    const onSubmit = async (values: FormValues) => {
        if (!organizationId) {
            return;
        }

        try {
            const {
                trimmedOpeningLine,
                firstVoiceStepIndex,
                hasStoredOpeningLine,
            } = resolveVoiceOpeningLine({
                steps,
                templateOpeningLine: openingLineFromMetadata || null,
                outreachType: outreach.outreach_type,
                isMultiShift,
            });
            // An unset v3 multi-shift line renders derived text; don't persist it.
            const keepsMultiShiftLineUnset =
                outreach.agent_generation === 'v3' &&
                !hasStoredOpeningLine &&
                usesSplitVoiceOpeningLine(outreach.outreach_type, isMultiShift);
            const openingLine = keepsMultiShiftLineUnset
                ? null
                : trimmedOpeningLine;
            const updatedSteps = serializeStepsForSubmission({
                steps,
                outreachType: outreach.outreach_type,
                trimmedOpeningLine: openingLine,
                firstVoiceStepIndex,
                isMultiShift,
                includeStepIds: outreach.agent_generation === 'v3',
            });

            if (
                !(await confirmClientIdentifyingInfo(
                    getOutreachSmsTemplateTexts(updatedSteps),
                ))
            ) {
                return;
            }

            setIsSubmitting(true);
            await getApiClient().patch({
                url: '/organizations/{organization_id}/callouts/{callout_id}',
                pathParams: {
                    organization_id: organizationId,
                    callout_id: outreach.id,
                },
                body: {
                    steps: updatedSteps,
                    ...(keepsMultiShiftLineUnset
                        ? {}
                        : { opening_line: openingLine }),
                    recurrence_schedule_description:
                        values.recurrenceScheduleDescription || null,
                },
            });

            toast.success('Outreach steps updated successfully', {
                persistNotification: true,
                notificationType: 'outreach_action',
            });
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Failed to update outreach steps:', error);
            toast.error(
                getApiErrorDetail(error) ?? 'Failed to update outreach steps',
                {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                },
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-4xl">
                <DialogHeader>
                    <DialogTitle>Edit Outreach Steps</DialogTitle>
                    <DialogDescription>
                        Update the steps and messaging for this outreach.
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-4"
                    >
                        <OutreachStepsPreview
                            steps={steps}
                            selectedShiftIds={selectedShifts.map(
                                (shift) => shift.id,
                            )}
                            selectedShifts={selectedShifts}
                            organization={organization}
                            outreachType={outreach.outreach_type}
                            form={form}
                            isLoadingInstructions={false}
                            onStepsChange={handleStepsChange}
                            templateSteps={templateSteps}
                            perShiftCarePlans={perShiftCarePlans}
                            originalSteps={outreach.steps}
                            templateOpeningLine={
                                openingLineFromMetadata || undefined
                            }
                            legacyShiftGroups={shiftGroups}
                            structuredShiftGroups={
                                outreach.shift_groups ?? null
                            }
                        />
                        <ClientIdentifyingInfoWarning
                            templates={getOutreachSmsTemplateTexts(steps)}
                        />
                        <div className="flex justify-end gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isSubmitting}>
                                {isSubmitting ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
            {clientIdentifyingInfoConfirmationDialog}
        </Dialog>
    );
}
